// @ts-nocheck
/**
 * 価格履歴パネルコンポーネント
 *
 * 期間選択 + ローソク足チャート + 統計情報を統合表示
 *
 * 使用場所:
 * - CardDetail（コレクション画面）
 * - MarketTab（CardDex）
 */

import React, { useState } from 'react';
import { CandlestickChart } from './CandlestickChart';
import {
  useCardPriceHistory,
  clearPriceHistoryCache,
  PERIOD_CONFIG,
  type PeriodType,
} from '../hooks/useCardPriceHistory';

// ========================================
// 型定義
// ========================================

interface PriceHistoryPanelProps {
  /** カードID */
  cardId: string;
  /** レアリティ倍率（価格に適用） */
  rarityMultiplier?: number;
  /** コンパクト表示モード */
  compact?: boolean;
  /** チャートの高さ */
  chartHeight?: number;
  /** 陽線の色 */
  bullColor?: string;
  /** 陰線の色 */
  bearColor?: string;
}

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  title: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  periodSelector: {
    display: 'flex',
    gap: '4px',
  },
  periodButton: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '500',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#a0a0a0',
  },
  periodButtonActive: {
    background: 'rgba(107, 76, 230, 0.5)',
    color: '#fff',
  },
  changeIndicator: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  chartContainer: {
    padding: '12px',
  },
  statsRow: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '12px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(0, 0, 0, 0.1)',
  },
  statItem: {
    textAlign: 'center' as const,
  },
  statLabel: {
    fontSize: '11px',
    color: '#808080',
    marginBottom: '4px',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#a0a0a0',
    fontSize: '14px',
  },
  error: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '30px',
    color: '#f87171',
    fontSize: '14px',
    textAlign: 'center' as const,
    gap: '12px',
  },
  retryButton: {
    padding: '8px 16px',
    fontSize: '12px',
    background: 'rgba(107, 76, 230, 0.3)',
    border: '1px solid rgba(107, 76, 230, 0.5)',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  noData: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#808080',
    fontSize: '14px',
    textAlign: 'center' as const,
    gap: '8px',
  },
  noDataIcon: {
    fontSize: '32px',
    opacity: 0.5,
  },
  noDataSubtext: {
    fontSize: '12px',
    color: '#606060',
  },
};

// ========================================
// サブコンポーネント
// ========================================

/** 期間選択ボタン */
function PeriodSelector({
  period,
  onChange,
  disabled,
}: {
  period: PeriodType;
  onChange: (p: PeriodType) => void;
  disabled?: boolean;
}) {
  const periods: PeriodType[] = ['24h', '7d', '30d', '90d'];

  return (
    <div style={styles.periodSelector}>
      {periods.map((p) => (
        <button
          key={p}
          style={{
            ...styles.periodButton,
            ...(period === p ? styles.periodButtonActive : {}),
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => !disabled && onChange(p)}
          onMouseEnter={(e) => {
            if (!disabled && period !== p) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (period !== p) {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }
          }}
          disabled={disabled}
        >
          {PERIOD_CONFIG[p].shortLabel}
        </button>
      ))}
    </div>
  );
}

/** 変動率インジケーター */
function ChangeIndicator({ changePercent }: { changePercent: number }) {
  const isPositive = changePercent > 0;
  const isNegative = changePercent < 0;

  const color = isPositive ? '#4ade80' : isNegative ? '#f87171' : '#808080';
  const icon = isPositive ? '📈' : isNegative ? '📉' : '➡️';
  const sign = isPositive ? '+' : '';

  return (
    <span style={{ ...styles.changeIndicator, color }}>
      {icon} {sign}{changePercent}%
    </span>
  );
}

/** 統計情報行 */
function StatsRow({
  stats,
  period,
}: {
  stats: { currentPrice: number; periodHigh: number; periodLow: number; changeAmount: number };
  period: PeriodType;
}) {
  const periodLabel = PERIOD_CONFIG[period].label;

  return (
    <div style={styles.statsRow}>
      <div style={styles.statItem}>
        <div style={styles.statLabel}>現在価格</div>
        <div style={styles.statValue}>{stats.currentPrice.toLocaleString()}G</div>
      </div>
      <div style={styles.statItem}>
        <div style={styles.statLabel}>{periodLabel}高値</div>
        <div style={{ ...styles.statValue, color: '#4ade80' }}>
          {stats.periodHigh.toLocaleString()}G
        </div>
      </div>
      <div style={styles.statItem}>
        <div style={styles.statLabel}>{periodLabel}安値</div>
        <div style={{ ...styles.statValue, color: '#f87171' }}>
          {stats.periodLow.toLocaleString()}G
        </div>
      </div>
      <div style={styles.statItem}>
        <div style={styles.statLabel}>変動額</div>
        <div style={{
          ...styles.statValue,
          color: stats.changeAmount > 0 ? '#4ade80' : stats.changeAmount < 0 ? '#f87171' : '#808080',
        }}>
          {stats.changeAmount > 0 ? '+' : ''}{stats.changeAmount.toLocaleString()}G
        </div>
      </div>
    </div>
  );
}

/** ローディング表示 */
function LoadingState() {
  return (
    <div style={styles.loading}>
      <span style={{ marginRight: '8px' }}>⏳</span>
      読み込み中...
    </div>
  );
}

/** エラー表示 */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={styles.error}>
      <span>⚠️ {message}</span>
      <button
        style={styles.retryButton}
        onClick={onRetry}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(107, 76, 230, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(107, 76, 230, 0.3)';
        }}
      >
        再試行
      </button>
    </div>
  );
}

/** データなし表示 */
function NoDataState() {
  return (
    <div style={styles.noData}>
      <div style={styles.noDataIcon}>📭</div>
      <div>データがありません</div>
      <div style={styles.noDataSubtext}>
        価格データが蓄積されると表示されます
      </div>
    </div>
  );
}

// ========================================
// メインコンポーネント
// ========================================

export function PriceHistoryPanel({
  cardId,
  rarityMultiplier = 1,
  compact = false,
  chartHeight = 150,
  bullColor = '#4ade80',
  bearColor = '#f87171',
}: PriceHistoryPanelProps) {
  const [period, setPeriod] = useState<PeriodType>('24h');

  const {
    data,
    stats,
    loading,
    error,
    refetch,
  } = useCardPriceHistory(cardId, period, { rarityMultiplier });

  // 期間変更ハンドラ（キャッシュをクリアして再取得）
  const handlePeriodChange = (newPeriod: PeriodType) => {
    if (newPeriod !== period) {
      // 新しい期間のキャッシュをクリア（強制再取得）
      clearPriceHistoryCache(cardId);
      setPeriod(newPeriod);
    }
  };

  // レアリティ倍率を適用したデータ
  const scaledData = data?.map(d => ({
    ...d,
    open: d.open * rarityMultiplier,
    high: d.high * rarityMultiplier,
    low: d.low * rarityMultiplier,
    close: d.close * rarityMultiplier,
  })) || null;

  return (
    <div style={styles.container}>
      {/* ヘッダー: タイトル + 期間選択 */}
      <div style={styles.header}>
        <div style={styles.title}>
          📊 価格推移
          {stats && !loading && (
            <ChangeIndicator changePercent={stats.changePercent} />
          )}
        </div>
        <PeriodSelector
          period={period}
          onChange={handlePeriodChange}
          disabled={loading}
        />
      </div>

      {/* チャート領域 */}
      <div style={styles.chartContainer}>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={refetch} />
        ) : scaledData && scaledData.length > 0 ? (
          <CandlestickChart
            data={scaledData}
            height={chartHeight}
            period={period}
            bullColor={bullColor}
            bearColor={bearColor}
            compact={compact}
          />
        ) : (
          <NoDataState />
        )}
      </div>

      {/* 統計情報 */}
      {stats && !loading && !error && scaledData && scaledData.length > 0 && (
        <StatsRow stats={stats} period={period} />
      )}
    </div>
  );
}

export default PriceHistoryPanel;
